import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  forwardRef,
  inject,
  Input,
  Output
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { maskitoNumberOptionsGenerator, maskitoParseNumber } from '@maskito/kit';
import { maskitoTransform, MaskitoOptions } from '@maskito/core';
import { MaskitoDirective } from '@maskito/angular';

// Shared factory so variants can re-use the currency formatting rules instead of restating them and
// risking drift. An empty prefix matches the generator's own default, so `buildCurrencyMask()`
// produces the mask this component has always used. Pass a negative `min` to allow negative amounts.
export function buildCurrencyMask(prefix: string = '', min: number = 0): MaskitoOptions {
  return maskitoNumberOptionsGenerator({
    precision: 2,
    thousandSeparator: ',',
    decimalSeparator: '.',
    minusSign: '-',
    min,
    prefix
  });
}

@Component({
  selector: 'app-currency-input',
  templateUrl: './currency-input.component.html',
  styleUrls: ['./currency-input.component.scss'],
  standalone: true,
  imports: [MaskitoDirective],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CurrencyInputComponent),
      multi: true
    }
  ],
  changeDetection: ChangeDetectionStrategy.Default
})
export class CurrencyInputComponent implements ControlValueAccessor {
  @Input() placeholder: string = '';
  @Input() controlId: string | null = null;
  @Input() error: boolean = false;
  @Input() mask: MaskitoOptions = buildCurrencyMask();
  @Output() valueChange = new EventEmitter<number | null>();

  public value: string = '';
  public disabled: boolean = false;

  private changeDetectorRef = inject(ChangeDetectorRef);

  private onChange: (value: number | null) => void = () => {};
  private onTouched: () => void = () => {};

  public writeValue(value: number | null): void {
    this.value = value === null ? '' : maskitoTransform(value?.toFixed(2), this.mask);
    this.changeDetectorRef.markForCheck();
  }

  public registerOnChange(fn: (value: number | null) => void): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  public setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.changeDetectorRef.markForCheck();
  }

  public handleInput(event: Event): void {
    const inputValue = (event.target as HTMLInputElement).value;
    this.value = inputValue;

    const numericValue = maskitoParseNumber(inputValue);
    this.valueChange.emit(isNaN(numericValue) ? null : numericValue);
    this.onChange(isNaN(numericValue) ? null : numericValue);
  }

  public handleBlur(): void {
    this.onTouched();
    const numericValue = maskitoParseNumber(this.value);
    if (!isNaN(numericValue)) {
      const formattedValue = numericValue.toFixed(2);
      this.value = maskitoTransform(formattedValue, this.mask);
    } else {
      this.value = '';
    }
  }
}
